import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { OAuthService } from 'angular-oauth2-oidc'

import { filter, fromEvent } from 'rxjs'

import { AuthConfigService } from '@flowx/designer/modules/auth/auth.service'
import { landingWorkspaceId } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { selectWorkspaceId } from '@flowx/designer/shared/store/selectors/router.selectors'

/** Where Go back leads. */
interface ReturnDestination {
  url: string
  workspaceId: string
}

/** The screen Go back returns to, and the login session it belongs to. */
interface ReturnTarget extends ReturnDestination {
  sid: string
}

const RETURN_TARGET_KEY = 'flx-return-target'

/**
 * A workspace-area screen. Running instances are left out: their start data travels in the
 * navigation state, so their URL alone would reopen them broken.
 */
const isReturnable = (url: string): boolean => {
  const path = url.split('?')[0]
  return path.startsWith('/guard/workspace/') && !path.endsWith('/instance')
}

const readStoredTarget = (): ReturnTarget | null => {
  try {
    return JSON.parse(localStorage.getItem(RETURN_TARGET_KEY) ?? 'null')
  } catch {
    return null
  }
}

/**
 * Go back from Organization Settings: the last workspace-area screen the user saw in this login
 * session, in any tab.
 *
 * Every navigation outside org settings is recorded, so a tab that crashes or is closed has already
 * left its screen behind. The target belongs to the Keycloak session (`sid`): one from an earlier
 * login, or another user's on the same browser, is never offered.
 */
@Injectable({ providedIn: 'root' })
export class ReturnTargetService {
  #router = inject(Router)
  #oauthService = inject(OAuthService)
  #destroyRef = inject(DestroyRef)
  #authConfigService = inject(AuthConfigService)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #workspaceId = inject(Store).selectSignal(selectWorkspaceId)

  /** What localStorage holds, which any tab may have written. */
  #stored = signal<ReturnTarget | null>(readStoredTarget())

  /**
   * The screen to return to, or `null` when this login session has none. The `sid` is read, not
   * tracked: it doesn't change within a session.
   */
  #target = computed(() => {
    const stored = this.#stored()
    return stored && stored.sid === this.#authConfigService.sessionId ? stored : null
  })

  goBackDestination = computed((): ReturnDestination | null => {
    const target = this.#target()

    if (!target) {
      return null
    }

    const workspaces = this.#workspacesAndPermissionsStore.availableWorkspacesForUser()

    if (workspaces.some(({ id }) => id === target.workspaceId)) {
      return target
    }

    // Go back falls back to the default workspace; the last-used one is where the logo leads.
    const defaultWorkspaceId = landingWorkspaceId(workspaces, null)

    return defaultWorkspaceId
      ? { url: `/guard/workspace/${defaultWorkspaceId}`, workspaceId: defaultWorkspaceId }
      : null
  })

  /** Starts recording. Call it once, as the app starts, so the first screen is recorded too. */
  init(): void {
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(({ urlAfterRedirects }) => isReturnable(urlAfterRedirects)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ urlAfterRedirects }) => this.#record(urlAfterRedirects))

    // Another tab recorded or cleared it.
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter(({ key }) => key === RETURN_TARGET_KEY),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#stored.set(readStoredTarget()))

    // Cleared with the session. A session that ends any other way is caught by the `sid` check.
    this.#oauthService.events
      .pipe(
        filter(({ type }) => type === 'logout'),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#clear())
  }

  goBack(): void {
    const destination = this.goBackDestination()

    if (destination) {
      this.#router.navigateByUrl(destination.url)
    }
  }

  #record(url: string): void {
    const sid = this.#authConfigService.sessionId
    const workspaceId = this.#workspaceId()

    if (!sid || !workspaceId) {
      return
    }

    const target = { url, workspaceId, sid }
    localStorage.setItem(RETURN_TARGET_KEY, JSON.stringify(target))
    this.#stored.set(target)
  }

  #clear(): void {
    localStorage.removeItem(RETURN_TARGET_KEY)
    this.#stored.set(null)
  }
}
